import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  fetchProjectsTotal,
  fetchPermissions,
  fetchUserById,
  patchUserLanguageExceptions,
} from '@app/api'
import config from '@app/config'

import { logout } from '@app/services'
import { isB2BView } from '@app/utils'
import { setRolesCookieFromActiveToken } from '@app/utils/token'

import { Permission } from '../../models'

export const getCurrentUserThunk = createAsyncThunk(
  'user/getCurrentUser',
  async ({ userId, accountId }: { userId: number; accountId: number }) => {
    setRolesCookieFromActiveToken()

    const isB2B = isB2BView()

    const [userResponse, permissions] = await Promise.all([
      fetchUserById(userId),
      isB2B
        ? fetchPermissions(userId).catch(() => [] as Permission[])
        : Promise.resolve<Permission[]>([]),
    ])

    if (userResponse.user?.isArchived) {
      const redirectUrl = !config.identity.isBackOffice
        ? `${config.identity.website}/logout`
        : null
      await logout(null, redirectUrl)
    }

    const account = userResponse.accounts.find((x) => x.id == accountId)
    if (!account) {
      await logout()
    }

    const isStaff = (userResponse.user.accountsRoles || []).some(
      (accountRole) =>
        accountRole.accountId === accountId && accountRole.role === 'Staff'
    )

    const shouldLoadProjectsCount =
      isB2B &&
      Boolean(account) &&
      !isStaff &&
      ['Franchise', 'Partner', 'Professional'].includes(account?.type || '')

    const projectsCount = shouldLoadProjectsCount
      ? await fetchProjectsTotal()
      : 0

    return {
      user: {
        ...userResponse.user,
        hasProjects: projectsCount > 0,
      },
      account: account!,
      permissions,
    }
  }
)

export const patchLanguageExceptionsThunk = createAsyncThunk(
  'user/patchLanguageExceptions',
  async ({ userId, language }: { userId: number; language: string }) => {
    const languageExceptions = [
      ...(config.regionalSettings?.languageExceptions || []),
    ]
    if (languageExceptions.includes(language)) {
      languageExceptions.splice(languageExceptions.indexOf(language), 1)
    } else {
      languageExceptions.push(language)
    }
    return await patchUserLanguageExceptions(userId, languageExceptions)
  }
)
